import {
  Box,
  Button,
  chakra,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AdminCaseClusterListViewQuery } from '@/@generated/codegen/API';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useTableQueryParamsUtils } from '@/hooks/useTableQueryParamsUtils';
import { useConfirmModal } from '@/ui/ConfirmModal/ConfirmModal';
import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters, { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';

import useAdminCaseClusterTableStore from './AdminCaseClusterListTableStore';
import getColumns, { AdminCaseClusterListItemType } from './columns';
import CreateCaseClusterForm from '../CreateCaseClusterForm/CreateCaseClusterForm';

type Props = {
  data: AdminCaseClusterListViewQuery;
  pagination: ReturnType<typeof usePaginationState>;
  handleDeleteCaseCluster: (caseClusterID: string) => void;
  tablePrefix: string;
};

const filterExtraInfo: FilterExtraInfo<AdminCaseClusterListItemType> = [
  {
    filterId: 'createdAt',
    filterType: FilterType.DATE,
  },
  {
    filterId: 'meetings',
    filterType: FilterType.STRING,
    queryParamKey: 'meetingName',
  },
  {
    filterId: 'clients',
    filterType: FilterType.STRING,
    queryParamKey: 'clientName',
  },
];

export default function AdminCaseClusterListView({
  data,
  pagination,
  handleDeleteCaseCluster,
  tablePrefix,
}: Props) {
  const { t } = useTranslation();
  const totalCount = data.listCaseClusters.totalCount;
  const [updateData, setUpdateData] = useState<AdminCaseClusterListItemType | null>(null);
  const { isOpen: isFormOpen, onOpen: openForm, onClose: closeForm } = useDisclosure();
  const confirmModalUtils = useConfirmModal();

  const customCloseForm = useCallback(() => {
    setUpdateData(null);
    closeForm();
  }, [closeForm]);

  const customOpenForm = useCallback(
    (newData: AdminCaseClusterListItemType | null) => {
      setUpdateData(newData);
      openForm();
    },
    [openForm]
  );

  const columns = getColumns({
    t,
    onDeleteClick: handleDeleteCaseCluster,
    onEditClick: (data) => {
      customOpenForm(data);
    },
    confirmModalUtils,
  });
  const {
    columnFilters,
    sorting,
    globalFilter,
    onColumnFiltersChange,
    onSortingChange,
    onGlobalFilterChange,
  } = useTableQueryParamsUtils(filterExtraInfo, tablePrefix);
  const [columnVisibility, setColumnVisibility, columnSizing, setColumnSizing] =
    useAdminCaseClusterTableStore((state) => [
      state.columnVisibility,
      state.setColumnVisibility,
      state.columnSizing,
      state.setColumnSizing,
    ]);

  const table = useReactTable({
    data: data.listCaseClusters.edges.map((edge) => edge.node),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.paginationState.pageSize),
    state: {
      pagination: pagination.paginationState,
      columnSizing,
      columnVisibility,
      sorting,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    onPaginationChange: pagination.createOnPaginationChangeFunc(data.listCaseClusters.pageInfo),
    onGlobalFilterChange: onGlobalFilterChange,
    onSortingChange: onSortingChange,
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columns: columns,
  });

  return (
    <Box maxW="full" p={{ base: 3, md: 3 }}>
      <Flex justifyContent="space-between" wrap="wrap" gap={2} alignItems="center" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold">
          {t('admin.caseClusters.caseClusterList.title')}
        </chakra.h3>
        <Modal isOpen={isFormOpen} onClose={customCloseForm}>
          <ModalOverlay />
          <ModalContent maxW={{ base: '95vw', md: 'md' }}>
            <ModalCloseButton />
            <ModalBody>
              <Box p="20px" gap="10px">
                <Heading size="sm" pb="5">
                  {updateData
                    ? t('admin.caseClusters.form.title.update')
                    : t('admin.caseClusters.form.title.create')}
                </Heading>

                <CreateCaseClusterForm data={updateData} closeForm={customCloseForm} />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Button
          background="blue.400"
          color="white"
          size={{ base: 'sm', md: 'md' }}
          data-test="create-meeting-button-popUp"
          onClick={() => {
            customOpenForm(null);
          }}
        >
          {t('admin.caseClusters.button.createNew')}
        </Button>
      </Flex>

      <>
        <Flex wrap="wrap" gap={2} alignItems="center" mb="4">
          <ColumnVisibility table={table} />
          <Filters
            columnFilters={columnFilters}
            setColumnFilters={onColumnFiltersChange}
            table={table}
            filterExtraInfo={filterExtraInfo}
          />
          <GlobalFilter
            globalFilter={globalFilter}
            setGlobalFilter={onGlobalFilterChange}
            maxWidth={{ base: 'none', md: '250px' }}
            flex={{ base: 1, md: 'initial' }}
            minW="150px"
          />
          <ActiveFilters
            columnFilters={columnFilters}
            removeItemFunc={(elem) => {
              onColumnFiltersChange(columnFilters.filter((f) => f.id !== elem.id));
            }}
            filterExtraInfo={filterExtraInfo}
          />
          <Pagination
            table={table}
            pageInfo={data.listCaseClusters.pageInfo}
            totalRows={totalCount}
            tablePrefix={tablePrefix}
            ml="auto"
          />
        </Flex>

        <TanstackTableView table={table} />
      </>
    </Box>
  );
}
